// src/context/AuthContext.tsx

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User, LoginRequest, RegisterRequest, ChangePasswordRequest } from '../types';
import { authApi } from '../api/auth';

interface AuthContextType {
    user: User | null;
    loading: boolean;
    isAuthenticated: boolean;
    mustChangePassword: boolean;   // ← НОВОЕ: удобный флаг
    login: (credentials: LoginRequest) => Promise<void>;
    register: (data: RegisterRequest) => Promise<void>;
    logout: () => Promise<void>;
    refreshUser: () => Promise<void>;
    changePassword: (data: ChangePasswordRequest) => Promise<void>;   // ← НОВОЕ
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const initAuth = async () => {
            try {
                await authApi.getCsrfToken();
                const response = await authApi.me();
                if (response.success && response.data) {
                    setUser(response.data);
                }
            } catch (error: any) {
                if (error?.response?.status !== 401) {
                    console.error('Ошибка инициализации auth:', error);
                }
                setUser(null);
            } finally {
                setLoading(false);
            }
        };

        initAuth();
    }, []);

    const login = useCallback(async (credentials: LoginRequest) => {
        await authApi.getCsrfToken();
        await authApi.login(credentials);
        const meResponse = await authApi.me();
        if (meResponse.success && meResponse.data) {
            setUser(meResponse.data);
        } else {
            throw new Error('Не удалось получить данные пользователя');
        }
    }, []);

    const register = useCallback(async (data: RegisterRequest) => {
        await authApi.getCsrfToken();
        await authApi.register(data);
        await authApi.getCsrfToken();
        await authApi.login({
            username: data.phoneNumber,
            password: data.password,
        });
        const meResponse = await authApi.me();
        if (meResponse.success && meResponse.data) {
            setUser(meResponse.data);
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            await authApi.logout();
        } catch (error) {
            console.error('Ошибка выхода:', error);
        } finally {
            setUser(null);
        }
    }, []);

    const refreshUser = useCallback(async () => {
        try {
            const response = await authApi.me();
            if (response.success && response.data) {
                setUser(response.data);
            }
        } catch (error) {
            setUser(null);
        }
    }, []);

    /**
     * Смена пароля — после успеха:
     * 1. Бэкенд вернёт обновлённого User
     * 2. Принудительно выходим из системы
     * 3. Фронт редиректит на /login
     */
    const changePassword = useCallback(async (data: ChangePasswordRequest) => {
        const response = await authApi.changePassword(data);
        if (!response.success) {
            throw new Error(response.error || 'Не удалось сменить пароль');
        }

        // Принудительный выход
        try {
            await authApi.logout();
        } catch (error) {
            console.error('Ошибка logout после смены пароля:', error);
            // Не падаем — главное, что пароль изменён
        } finally {
            setUser(null);
        }
    }, []);

    // Удобный флаг
    const mustChangePassword = !!user?.mustChangePassword;

    const value: AuthContextType = {
        user,
        loading,
        isAuthenticated: !!user,
        mustChangePassword,
        login,
        register,
        logout,
        refreshUser,
        changePassword,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};